/**
 * Typed bridge over veto-crypto WASM — SPEC §7.1 / NFR-NAT-003.
 * UI must never hold raw long-term private keys beyond vault-wrapped storage.
 */
import init, {
  WasmIdentity,
  WasmInbound,
  WasmSessionRecord,
  compactBundleInfo,
  conversationIdOf,
  safetyNumber,
  sealEnvelope,
  sessionSuiteV2,
  suite_id,
  wrapVaultKey,
  unlockVaultKey,
  rewrapVaultKey,
  vaultNeedsRewrap,
  verifyCompactBundle,
  verifyMessagingSig,
  verifyIdentityBundle,
} from "../pkg/veto_crypto_wasm.js";
import { schnorr } from "@noble/curves/secp256k1.js";

let ready: Promise<void> | null = null;

export async function initCrypto(): Promise<void> {
  if (!ready) {
    ready = (async () => {
      // Node/vitest: load WASM bytes from disk (no Vite @fs fetch).
      // Browser builds use the else branch; Vite externalizes node: builtins.
      const proc = (globalThis as { process?: { versions?: { node?: string } } }).process;
      const isNode = Boolean(proc?.versions?.node);
      if (isNode) {
        /* Dynamic string import so the web TS program does not resolve node: builtins. */
        type NodeFs = { readFileSync: (path: string) => Uint8Array };
        type NodePath = { dirname: (p: string) => string; join: (...p: string[]) => string };
        type NodeUrl = { fileURLToPath: (u: string | URL) => string };
        const fsId = ["node", "fs"].join(":");
        const pathId = ["node", "path"].join(":");
        const urlId = ["node", "url"].join(":");
        const { readFileSync } = (await import(/* @vite-ignore */ fsId)) as unknown as NodeFs;
        const { dirname, join } = (await import(/* @vite-ignore */ pathId)) as unknown as NodePath;
        const { fileURLToPath } = (await import(/* @vite-ignore */ urlId)) as unknown as NodeUrl;
        const wasmPath = join(dirname(fileURLToPath(import.meta.url)), "../pkg/veto_crypto_wasm_bg.wasm");
        await init({ module_or_path: readFileSync(wasmPath) });
      } else {
        await init();
      }
    })();
  }
  await ready;
}

export {
  WasmIdentity,
  WasmInbound,
  WasmSessionRecord,
  conversationIdOf,
  safetyNumber,
  sealEnvelope,
  sessionSuiteV2,
  wrapVaultKey,
  unlockVaultKey,
  rewrapVaultKey,
  vaultNeedsRewrap,
  verifyCompactBundle,
  verifyMessagingSig,
  verifyIdentityBundle,
};

/** Keys extracted from a VERIFIED compact bundle (lowercase hex). */
export type CompactBundleKeys = { ed: string; x25519: string; transport: string; hybrid: string };

/** Verify + decode a compact (v2) identity bundle; null when invalid. */
export function readCompactBundle(bytes: Uint8Array): CompactBundleKeys | null {
  try {
    return compactBundleInfo(bytes) as CompactBundleKeys;
  } catch {
    return null;
  }
}

/** Inner message kinds returned by `WasmIdentity.openEnvelope`. */
export const INBOUND_PREKEY = 1;
export const INBOUND_NORMAL = 2;
// Protocol v2: WasmSessionRecord (AKE + Double Ratchet) is the only session type
// in production builds; the retired v1 WasmSession exists only in test-vectors builds.
// WasmIdentity.transportPublicKey / transportSign expose BIP-340 x-only pubkey +
// Schnorr signing for NIP-01 transport events — secret stays inside the core.
// bobFromShared / exportSeeds / hybridSecretParts / respond(secrets) gated behind `test-vectors`
// (AUDIT-BRIDGE-001/002). Production uses sealExport / fromSealedExport / respondWithIdentity.

/**
 * BIP-340 Schnorr verification for NIP-01 transport events. Public-key only —
 * no secrets cross to JS (SEC-1-4); signing stays in the Rust core.
 */
export function verifyTransportSig(
  pkXOnly: Uint8Array,
  message32: Uint8Array,
  sig64: Uint8Array,
): boolean {
  try {
    return schnorr.verify(sig64, message32, pkXOnly);
  } catch {
    return false;
  }
}

/**
 * Throwaway BIP-340 key for exactly one NIP-01 event (NIP-59 style). Relays
 * can verify the event, but the key links to nothing: sender identity lives
 * inside the sealed envelope, authenticated by the v2 session.
 */
export function ephemeralEventSigner(): { pubkeyHex: string; sign: (id32: Uint8Array) => Uint8Array } {
  const sk = schnorr.utils.randomSecretKey();
  const pubkeyHex = toHex(schnorr.getPublicKey(sk));
  let used = false;
  return {
    pubkeyHex,
    sign: (id32: Uint8Array) => {
      if (used) throw new Error("ephemeral event key already used");
      used = true;
      const sig = schnorr.sign(id32, sk);
      sk.fill(0);
      return sig;
    },
  };
}

export function suiteId(): string {
  return suite_id();
}

export function randomBytes(len: number): Uint8Array {
  const out = new Uint8Array(len);
  crypto.getRandomValues(out);
  return out;
}

export function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export function fromHex(hex: string): Uint8Array {
  const clean = hex.replace(/\s+/g, "");
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
}

/**
 * @deprecated Sync XOR-fold fallback — NOT a cryptographic hash. No production
 * caller may use this for conversation ids, relay-visible tags, or anything
 * adversarially observed (SPEC §9.5). Use async {@link sha256ConversationId}.
 * Kept only to avoid breaking old imports; do not add new callers.
 */
export function conversationIdFromKeys(a: Uint8Array, b: Uint8Array): Uint8Array {
  const sorted = [toHex(a), toHex(b)].sort();
  const data = new TextEncoder().encode(`veto/v1/conv|${sorted[0]}|${sorted[1]}`);
  return crypto.subtle
    ? // sync fallback hash via blake-like: use SHA-256 through Subtle later; sync for now
      (() => {
        // Deterministic non-crypto id for local demos if subtle unavailable synchronously —
        // callers should prefer async sha256ConversationId.
        const out = new Uint8Array(32);
        for (let i = 0; i < data.length; i++) out[i % 32] ^= data[i];
        return out;
      })()
    : new Uint8Array(32);
}

export async function sha256ConversationId(a: Uint8Array, b: Uint8Array): Promise<Uint8Array> {
  const sorted = [toHex(a), toHex(b)].sort();
  const data = new TextEncoder().encode(`veto/v1/conv|${sorted[0]}|${sorted[1]}`);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return new Uint8Array(digest);
}
